import { useEffect, useRef, useState } from 'react';
import ReactPhoneInput2, { PhoneInputProps as ReactPhoneInputProps } from 'react-phone-input-2';
import { Button, Chip, IconButton, Stack, styled, SxProps } from '@mui/material';
import { IconBackspace, IconPencil } from '@tabler/icons-react';

import 'react-phone-input-2/lib/style.css';

const StyledReactPhoneInput = styled(ReactPhoneInput2)({});

interface PhoneInputProps extends ReactPhoneInputProps {
  sx?: SxProps;
  inputSx?: SxProps;
  quantity?: string;
  isDropdownUp?: boolean;
  dropdownHeight?: string;
  showSideDetails?: boolean;
  onClickToEdit?: () => void;
  onBackspace?: () => void;
  isDisabled?: boolean;
  showBackspace?: boolean;
  showCountryFlag?: boolean;
  isInvalid?: boolean;
}

export default function PhoneInput({
  sx,
  inputSx,
  onClickToEdit,
  quantity,
  isDisabled,
  isInvalid,
  dropdownHeight = '200px',
  isDropdownUp,
  showSideDetails = false,
  showBackspace = false,
  onBackspace,
  showCountryFlag = true,
  country = 'us',
  ...props
}: PhoneInputProps) {
  const [width, setWidth] = useState<number>(0);
  const elementRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const updateWidth = () => {
      if (elementRef.current) {
        setWidth(elementRef.current?.offsetWidth);
      }
    };

    updateWidth();

    window.addEventListener('resize', updateWidth);
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  useEffect(() => {
    const element = elementRef.current;

    if (!element) return;

    const resizeObserver = new ResizeObserver((entries) => {
      if (entries.length === 0) return null;
      const entry = entries[0];
      setWidth(entry.contentRect.width);
    });

    resizeObserver.observe(element);

    return () => {
      resizeObserver.unobserve(element);
    };
  }, []);

  return (
    <Stack
      component="div"
      ref={elementRef}
      alignItems="center"
      flexDirection="row"
      aria-disabled={isDisabled}
      aria-invalid={isInvalid}
      sx={{
        zIndex: '2',
        width: '100%',
        height: '36px',
        borderRadius: '8px',
        background: (theme) => (theme.palette.mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[50]),
        outline: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[400]} !important`,
        ':focus-within': {
          outline: (theme) => `2px solid ${theme.palette.primary[600]} !important`
        },
        position: 'relative',
        '&[aria-disabled="true"]': {
          background: (theme) => (theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100]),
          '&>*>.flag-dropdown': {
            pointerEvents: 'none',
            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100])
          }
        },
        '&[aria-invalid="true"]': {
          outline: (theme) => `2px solid ${theme.palette.error[500]} !important`,
          ':focus-within': {
            outline: (theme) => `2px solid ${theme.palette.error[500]} !important`
          }
        },
        ...sx
      }}
    >
      <StyledReactPhoneInput
        country={country}
        {...props}
        sx={{
          gap: '14px !important',
          width: '100%',
          height: '100% !important',
          display: 'flex',
          borderRadius: '8px',
          flexDirection: 'row',
          alignItems: 'center',
          background: 'transparent !important',
          fontFamily: `'Poppins', sans-serif`,
          border: 'none !important',
          '&.react-tel-input>.flag-dropdown': {
            padding: 0,
            width: '52px',
            height: '100%',
            display: 'grid',
            margin: '0 6px 0 0',
            placeItems: 'center',
            transition: '.3s all ease',
            borderRadius: '8px 0 0 8px !important',
            border: '0px solid transparent !important',
            borderRight: (theme) => `1px solid ${theme.palette.mode === 'dark' ? theme.palette.divider : theme.palette.grey[300]} !important`,
            backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100]),
            ':hover': {
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100])
            },
            '&:.open': {
              borderRadius: '8px 0 0 8px !important',
              backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.grey[100]),
              '&>.selected-flag': {
                backgroundColor: 'transparent !important'
              }
            },
            '&>.country-list': {
              top: isDropdownUp ? `calc(-${dropdownHeight} - 8px) !important` : '118% !important',
              left: '0 !important',
              borderRadius: '8px',
              margin: '0 !important',
              height: dropdownHeight,
              maxHeight: dropdownHeight,
              padding: '12px 0',
              boxShadow: (theme) => theme.shadows[20],
              width: `${width}px !important`,
              backgroundColor: (theme) => theme.palette.background.paper,
              '&>li': {
                height: '38px',
                display: 'flex',
                padding: '0 14px',
                alignItems: 'center',
                fontFamily: (theme) => theme.typography.fontFamily,
                '&.highlight, &:hover': {
                  backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.dark[800] : theme.palette.grey[100])
                },
                '&>.country-name': {
                  fontWeight: '500',
                  color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.text.primary : theme.palette.grey[700])
                },
                '&>.dial-code': {
                  fontSize: '14px',
                  color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.text.secondary : theme.palette.grey[500])
                }
              }
            },
            '&>.selected-flag': {
              padding: 0,
              width: '100%',
              height: '100%',
              display: 'grid',
              borderRadius: '8px !important',
              placeItems: 'center',
              backgroundColor: 'transparent !important',
              border: '0px solid transparent',
              '&>.flag': {
                transform: 'translate(-6px)'
              },
              ':hover': {
                backgroundColor: 'transparent !important'
              }
            }
          },
          '&>input': {
            outline: 'none',
            borderRadius: '8px !important',
            height: '100% !important',
            width: '100% !important',
            fontSize: '16px !important',
            fontWeight: '500',
            paddingLeft: `${showCountryFlag ? '64px' : '16px'} !important`,
            fontFamily: (theme) => theme.typography.fontFamily,
            color: (theme) => theme.palette.text.primary,
            border: '0px solid transparent !important',
            backgroundColor: 'transparent !important',
            background: 'transparent !important'
          },
          ...(!showCountryFlag && {
            '&>.flag-dropdown': {
              display: 'none'
            }
          }),
          ...inputSx
        }}
      />
      {showSideDetails && (
        <Stack
          flexDirection="row"
          gap="6px"
          alignItems="center"
          sx={{
            marginRight: '8px'
          }}
        >
          {quantity && quantity?.length > 1 && (
            <Chip
              sx={{
                fontWeight: 600,
                height: '28px',
                lineHeight: 1,
                paddingX: 1,
                paddingY: 0.5,
                '.MuiChip-label': {
                  padding: 0
                }
              }}
              label={quantity}
            />
          )}
          <IconButton sx={{ height: '32px', width: '32px', borderRadius: '8px' }} color="default" size="small" onClick={onClickToEdit}>
            <IconPencil size={16} />
          </IconButton>
        </Stack>
      )}
      {showBackspace && (
        <Button
          aria-label="Backspace"
          sx={{
            padding: 0,
            width: '2rem',
            height: '2rem',
            display: 'grid',
            minWidth: '2rem',
            placeItems: 'center',
            margin: '0 14px 0 0',
            color: (theme) => theme.palette.grey[600],
            '&:hover': {
              background: (theme) => theme.palette.grey[100]
            }
          }}
          onClick={onBackspace}
        >
          <IconBackspace size={24} />
        </Button>
      )}
    </Stack>
  );
}
